import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { formatTimestamp } from "../../lib/format";
import { useAdminAudit } from "../../lib/queries";
import { EmptyRow, PageError, Pill, describeAction } from "./shared";

const ACTORS = [{ value: "", label: "Todos" }, { value: "admin", label: "Administradores" }, { value: "customer", label: "Clientes" }, { value: "service", label: "Worker" }, { value: "agent", label: "Aliados" }];
const ACTOR_LABEL: Record<string, string> = { admin: "Administrador", customer: "Cliente", service: "Worker", agent: "Aliado", system: "Sistema" };
const OUTCOMES = [{ value: "", label: "Cualquier resultado" }, { value: "success", label: "Éxito" }, { value: "failure", label: "Fallo" }, { value: "denied", label: "Denegado" }, { value: "pending", label: "Pendiente" }];

/** The audit chain, most recent first, with the filters an incident review actually needs. */
export function AdminAuditPage() {
  usePageHeader("Bitácora", "Cadena de auditoría inmutable: cada acción de clientes, administradores, aliados y del worker.");
  const events = useAdminAudit(200);
  const [actor, setActor] = useState("");
  const [outcome, setOutcome] = useState("");
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => (events.data ?? []).filter((event) => {
    if (actor && event.actorType !== actor) return false;
    if (outcome && event.outcome !== outcome) return false;
    if (search) {
      const needle = search.toLowerCase();
      const haystack = `${event.action} ${describeAction(event.action)} ${event.targetType ?? ""} ${event.targetId ?? ""} ${JSON.stringify(event.metadata)}`.toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  }), [events.data, actor, outcome, search]);
  return (
    <div className="page-container">
      <div className="discover-toolbar">
        <label className="search-field">
          <Search size={18} aria-hidden="true" />
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Acción, objetivo o dato del evento" aria-label="Buscar en la bitácora" />
        </label>
        <div className="chip-row" role="tablist" aria-label="Filtrar por actor">
          {ACTORS.map((item) => <button key={item.value || "all"} type="button" role="tab" aria-selected={actor === item.value} className={`chip${actor === item.value ? " active" : ""}`} onClick={() => setActor(item.value)}>{item.label}</button>)}
        </div>
        <select className="standalone-input small" value={outcome} onChange={(e) => setOutcome(e.target.value)} aria-label="Filtrar por resultado">
          {OUTCOMES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </select>
        <span className="toolbar-count">{filtered.length} de {events.data?.length ?? 0} eventos</span>
      </div>
      {events.isLoading && <div className="page-state"><Spinner label="Cargando bitácora" /></div>}
      {events.isError && <PageError title="No pudimos cargar la bitácora" retry={() => void events.refetch()} />}
      {events.data && (
        <div className="table-wrap">
          <table className="admin-table">
            <thead><tr><th>Cuándo</th><th>Acción</th><th>Actor</th><th>Resultado</th><th>Objetivo</th><th>Detalle</th></tr></thead>
            <tbody>
              {filtered.map((event) => (
                <tr key={event.id} className={event.outcome === "failure" || event.outcome === "denied" ? "row-bad" : undefined}>
                  <td>{formatTimestamp(event.createdAt)}</td>
                  <td><div className="table-primary"><strong>{describeAction(event.action)}</strong><span className="mono">{event.action}</span></div></td>
                  <td>{ACTOR_LABEL[event.actorType] ?? event.actorType}</td>
                  <td><Pill tone={event.outcome === "success" ? "ok" : event.outcome === "pending" ? "warn" : "bad"} label={OUTCOMES.find((item) => item.value === event.outcome)?.label ?? event.outcome} /></td>
                  <td><div className="table-primary"><span>{event.targetType ?? "—"}</span>{event.targetId && <span className="mono small" translate="no">{event.targetId}</span>}</div></td>
                  <td><Metadata value={event.metadata} /></td>
                </tr>
              ))}
              {filtered.length === 0 && <EmptyRow colSpan={6}>Sin eventos con estos filtros.</EmptyRow>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Metadata({ value }: { value: Record<string, unknown> }) {
  const entries = Object.entries(value ?? {}).filter(([, item]) => item !== null && item !== undefined && item !== "");
  if (!entries.length) return <span className="muted">—</span>;
  return (
    <span className="meta-inline" translate="no">
      {entries.slice(0, 5).map(([key, item]) => <span key={key}><b>{key}</b> {typeof item === "object" ? JSON.stringify(item) : String(item)}</span>)}
      {entries.length > 5 && <span>+{entries.length - 5}</span>}
    </span>
  );
}
